// ===================================================================
// utils/vocabExtract.ts
// Extrait des entrées de vocabulaire "propres" (mot isolé + traduction)
// depuis les données de catégories existantes, pour les mini-jeux qui
// ont besoin d'un mot unique plutôt que d'une phrase (Mot du jour,
// Memory Duel...).
// ===================================================================
import { getLanguageData } from '../data/languages';
import { LanguageCode, WordData } from '../types/types';

export interface VocabEntry {
  word: string;
  clue: string;
  clueEn?: string;
  illustration?: string;
  category?: string;
}

// Articles de classe nominale wolof, accolés au nom dans les clés
// existantes ("Xaj bi (Chien)") : on les retire pour isoler le mot.
const WOLOF_ARTICLES = new Set(['bi', 'wi', 'gi', 'mi', 'ji', 'si', 'ki', 'li', 'yi', 'ñi', 'ñu']);

const LETTERS_ONLY = /^[\p{L}]+$/u;

function cleanKey(languageCode: LanguageCode, key: string): string | null {
  // Copte : la clé est le mot en écriture copte, la translitération
  // latine (la seule saisissable au clavier) est entre parenthèses.
  if (languageCode === 'co') {
    const m = key.match(/\(([^)]+)\)/);
    return m ? m[1].trim() : null;
  }

  const beforeParen = key.split(' (')[0].trim();
  if (!beforeParen) return null;

  const tokens = beforeParen.split(/\s+/);
  if (tokens.length === 1) return tokens[0];

  if (languageCode === 'wf' && tokens.length === 2 && WOLOF_ARTICLES.has(tokens[1].toLowerCase())) {
    return tokens[0];
  }

  // Expression composée (ex: "Xonq curr", "Mbaam àll") : pas adaptée
  // à un jeu mot-par-mot, on l'écarte plutôt que de la tronquer à tort.
  return null;
}

/** Vocabulaire "mot unique" utilisable pour Wordle / Memory, dédupliqué. */
export function getVocabEntries(languageCode: LanguageCode): VocabEntry[] {
  if (languageCode === 'ka') return []; // hiéroglyphes : pas de saisie clavier possible

  const data = getLanguageData(languageCode);
  const seen = new Set<string>();
  const entries: VocabEntry[] = [];

  Object.entries(data.categories).forEach(([categoryName, words]) => {
    if (categoryName === 'Grammaire') return;

    Object.entries(words as Record<string, WordData>).forEach(([key, value]) => {
      if (!value || typeof value !== 'object' || typeof (value as WordData).translation !== 'string') return;

      const word = cleanKey(languageCode, key);
      if (!word || !LETTERS_ONLY.test(word)) return;

      const dedupeKey = word.toLocaleUpperCase();
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);

      entries.push({
        word,
        clue: (value as WordData).translation,
        clueEn: (value as WordData).translationEn,
        illustration: (value as WordData).illustration,
        category: categoryName,
      });
    });
  });

  return entries;
}

/** Mots de 3 à 8 lettres, adaptés à une grille façon Wordle. */
export function getWordlePool(languageCode: LanguageCode): VocabEntry[] {
  return getVocabEntries(languageCode).filter(e => e.word.length >= 3 && e.word.length <= 8);
}

/** Mots courts avec une traduction concise, adaptés aux cartes du Memory. */
export function getMemoryPool(languageCode: LanguageCode): VocabEntry[] {
  return getVocabEntries(languageCode).filter(e => e.word.length <= 16 && e.clue.length <= 22);
}

// ── La forge des mots ─────────────────────────────────────────────
export const FORGE_WORDS_PER_GAME = 6;
const FORGE_MIN_LETTERS = 2;
const FORGE_MAX_LETTERS = 6; // au-delà, les cases ne tiennent plus sur l'enclume

/** Lettres d'un mot pour la forge : minuscules, accents composés (ñ, ë, à…). */
export function forgeLetters(word: string): string[] {
  return Array.from(word.normalize('NFC').toLocaleLowerCase());
}

export interface ForgeTheme {
  category: string;
  entries: VocabEntry[];
}

/**
 * Thèmes jouables pour la forge : catégories avec assez de mots courts.
 * Si aucune catégorie n'en a assez, un thème "mélange" regroupe tout.
 */
export function getForgeThemes(languageCode: LanguageCode): ForgeTheme[] {
  const all = getVocabEntries(languageCode).filter(e => {
    const n = forgeLetters(e.word).length;
    return n >= FORGE_MIN_LETTERS && n <= FORGE_MAX_LETTERS;
  });
  const byCategory = new Map<string, VocabEntry[]>();
  all.forEach(e => {
    const c = e.category ?? '';
    byCategory.set(c, [...(byCategory.get(c) ?? []), e]);
  });
  const themes = Array.from(byCategory.entries())
    .filter(([, entries]) => entries.length >= FORGE_WORDS_PER_GAME)
    .map(([category, entries]) => ({ category, entries }));
  if (themes.length === 0 && all.length >= FORGE_WORDS_PER_GAME) {
    return [{ category: '', entries: all }];
  }
  return themes;
}

/** Toutes les lettres de la langue, pour tirer des lettres intruses crédibles. */
export function getForgeLetterPool(languageCode: LanguageCode): string[] {
  const set = new Set<string>();
  getVocabEntries(languageCode).forEach(e => forgeLetters(e.word).forEach(ch => set.add(ch)));
  return Array.from(set);
}
